import React, { useState } from 'react';
import { authAxios } from '../utils/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, Check, X, Loader2 } from 'lucide-react';

export default function AIQuestionModal({ 
  open, 
  onClose, 
  episodes, 
  currentSeason, 
  API, 
  onQuestionApproved 
}) {
  const [step, setStep] = useState(1);
  const [selectedEpisodes, setSelectedEpisodes] = useState([]);
  const [adminPrompt, setAdminPrompt] = useState('');
  const [drafts, setDrafts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);

  const resetAndClose = () => {
    setStep(1);
    setSelectedEpisodes([]);
    setAdminPrompt('');
    setDrafts([]);
    onClose();
  };

  const toggleEpisode = (epId) => {
    setSelectedEpisodes(prev => 
      prev.includes(epId) ? prev.filter(id => id !== epId) : [...prev, epId]
    );
  };

  const generateQuestions = async () => {
    if (selectedEpisodes.length === 0) {
      toast.error('Please select at least one episode');
      return;
    }
    setGenerating(true);
    try {
      const res = await authAxios.post(
        `${API}/bonus-questions/ai-generate`,
        { episode_ids: selectedEpisodes, admin_prompt: adminPrompt || null }
      );
      setDrafts(res.data.drafts || []);
      setStep(2);
      toast.success(`Generated ${res.data.count} question(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const regenerateQuestion = async (draft) => {
    setRegenerating(draft.id);
    try {
      const res = await authAxios.post(
        `${API}/bonus-questions/ai-regenerate?episode_id=${draft.episode_id}${adminPrompt ? `&admin_prompt=${encodeURIComponent(adminPrompt)}` : ''}`
      );
      setDrafts(prev => prev.map(d => d.id === draft.id ? res.data.draft : d));
      toast.success('Question regenerated');
    } catch (error) {
      toast.error('Failed to regenerate');
    } finally {
      setRegenerating(null);
    }
  };

  const updateDraft = (draftId, field, value) => {
    setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, [field]: value } : d));
  };

  const approveDraft = async (draft) => {
    const episode = episodes.find(ep => ep.id === draft.episode_id);
    try {
      await authAxios.post(
        `${API}/bonus-questions`,
        {
          season_id: currentSeason.id,
          episode_id: draft.episode_id,
          air_date: episode?.air_date?.split('T')[0] || '',
          question_text: draft.question_text,
          answer_type: draft.answer_type,
          options: draft.options || [],
          points_value: draft.points_value,
          is_published: false,
          allow_answer_edits: false
        }
      );
      setDrafts(prev => prev.filter(d => d.id !== draft.id));
      toast.success('Question approved!');
      onQuestionApproved();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const rejectDraft = (draftId) => {
    setDrafts(prev => prev.filter(d => d.id !== draftId));
    toast.success('Draft rejected');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && resetAndClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            AI Question Creation
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 mt-4">
            <div>
              <Label className="font-semibold">Select Episodes</Label>
              <p className="text-sm text-slate-500 mb-2">Choose episodes to generate questions for</p>
              <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto border rounded p-2">
                {episodes.map((ep, index) => (
                  <div
                    key={ep.id}
                    onClick={() => toggleEpisode(ep.id)}
                    className={`p-2 rounded cursor-pointer border flex items-center gap-2 ${
                      selectedEpisodes.includes(ep.id) ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Checkbox checked={selectedEpisodes.includes(ep.id)} />
                    <span className="text-sm">Ep {ep.number || index + 1}</span>
                  </div>
                ))}
              </div>
              {episodes.length === 0 && (
                <p className="text-sm text-slate-500 py-4 text-center">No episodes. Set up Episode Calendar first.</p>
              )}
            </div>
            <div>
              <Label className="font-semibold">Preferences (Optional)</Label>
              <Textarea
                value={adminPrompt}
                onChange={(e) => setAdminPrompt(e.target.value)}
                placeholder="e.g., Focus on challenges, prefer yes/no questions..."
                rows={2}
              />
            </div>
            <div className="bg-slate-50 rounded p-3 text-xs text-slate-600">
              <p className="font-medium">AI Guardrails:</p>
              <ul className="list-disc list-inside mt-1">
                <li>No spoilers - questions for predictions only</li>
                <li>Drafts require your approval before going live</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={resetAndClose}>Cancel</Button>
              <Button onClick={generateQuestions} disabled={generating || selectedEpisodes.length === 0}>
                {generating ? (
                  <span className="flex items-center"><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</span>
                ) : (
                  <span className="flex items-center"><Sparkles className="w-4 h-4 mr-2" />Generate</span>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">{drafts.length} draft(s) to review</p>
              <Button variant="outline" size="sm" onClick={() => setStep(1)}>Back</Button>
            </div>
            {drafts.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <p>All drafts processed.</p>
                <Button variant="outline" className="mt-2" onClick={() => setStep(1)}>Generate More</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {drafts.map(draft => (
                  <Card key={draft.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between mb-2">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Ep {draft.episode_number}</span>
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">
                          {draft.answer_type === 'yes_no' ? 'Yes/No' : draft.answer_type === 'multiple_choice' ? 'Multiple Choice' : 'Free Text'}
                        </span>
                      </div>
                      <Textarea
                        value={draft.question_text}
                        onChange={(e) => updateDraft(draft.id, 'question_text', e.target.value)}
                        rows={2}
                        className="mb-2"
                      />
                      {draft.answer_type === 'multiple_choice' && draft.options && (
                        <div className="space-y-1 mb-2">
                          {draft.options.map((opt, idx) => (
                            <Input
                              key={idx}
                              value={opt}
                              onChange={(e) => {
                                const newOpts = [...draft.options];
                                newOpts[idx] = e.target.value;
                                updateDraft(draft.id, 'options', newOpts);
                              }}
                              className="text-sm"
                              placeholder={`Option ${idx + 1}`}
                            />
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-3">
                        <Label className="text-xs">Points:</Label>
                        <Input
                          type="number"
                          value={draft.points_value}
                          onChange={(e) => updateDraft(draft.id, 'points_value', parseInt(e.target.value) || 5)}
                          className="w-16 text-sm"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t">
                        <Button size="sm" variant="outline" onClick={() => rejectDraft(draft.id)} className="text-red-600">
                          <X className="w-3 h-3 mr-1" />Reject
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => regenerateQuestion(draft)} disabled={regenerating === draft.id}>
                          {regenerating === draft.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Refresh
                        </Button>
                        <Button size="sm" onClick={() => approveDraft(draft)} className="bg-emerald-600 hover:bg-emerald-700">
                          <Check className="w-3 h-3 mr-1" />Approve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={resetAndClose}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
